<template>
  <base-screen-card
    :reference="reference"
    :hide-large-content="hideAbstracts"
    :hide="hide"
    :animate-open="animateOpen"
    :highlights="searchHighlights"
    :show-full-text="fullTextReview"
    @open="$emit('open')"
    @edit-field="$emit('edit-field', $event)"
    @hidden="onHiddenAnimationComplete"
    @animated-open="$emit('animated-open')"
  >
    <template #details>
      <div
        v-if="showVoteDetails"
        class="vote-details-section px-4 py-2"
      >
        <div
          class="expand-button primary--text"
          data-testid="review-details-toggle"
          @click="voteDetailsExpanded = !voteDetailsExpanded"
        >
          <v-icon color="primary" small>
            {{ voteDetailsExpanded ? 'expand_less' : 'expand_more' }}
          </v-icon>
          Review details
        </div>
        <vote-details-list
          v-show="voteDetailsExpanded"
          class="mt-2"
          :reference="reference"
          :current-researcher="currentResearcher"
        />
      </div>
    </template>
    <template #actions>
      <screen-card-actions
        :review="review"
        :reference="reference"
        :current-researcher="currentResearcher"
        :voting-only="votingOnly"
        :full-text-review="fullTextReview"
        @create-vote="onCreateVote"
        @update-vote="onUpdateVote"
        @revoke-vote="onRevokeVote"
        @resolve-conflict="onResolveConflict"
        @update-outcome="onUpdateOutcome"
        @revoke-outcome="$emit('revoke-outcome')"
      />
    </template>
  </base-screen-card>
</template>

<script>
import BaseScreenCard from './BaseScreenCard';
import ScreenCardActions from './ScreenCardActions';
import VoteDetailsList from '@/components/References/VoteDetailsList';
import {
  Review,
  Reference,
  Researcher,
  ReviewerScreeningVote,
  FullTextReviewVote,
  ScreeningOutcome,
  FullTextReviewOutcome,
} from '@/models';
import { ScreeningOutcomeResolution } from '@/enums';
import { mapActions } from 'vuex';
import { cloneDeep } from 'lodash';

export default {
  name: 'ScreenCard',
  components: {
    BaseScreenCard,
    ScreenCardActions,
    VoteDetailsList,
  },
  props: {
    review: {
      type: Review,
      required: true,
    },
    reference: {
      type: Reference,
      required: true,
    },
    currentResearcher: {
      type: Researcher,
      required: true,
    },
    hideAbstracts: {
      type: Boolean,
      default: false,
    },
    hide: {
      type: Boolean,
      default: false,
    },
    animateOpen: {
      type: Boolean,
      default: false,
    },
    searchHighlights: {
      type: Array,
      default: () => [],
    },
    votingOnly: {
      type: Boolean,
      default: false,
    },
    fullTextReview: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'open',
    'edit-field',
    'saving-vote',
    'saved-vote',
    'revoking-vote',
    'revoked-vote',
    'saving-outcome',
    'saved-outcome',
    'revoke-outcome',
  ],
  data: () => ({
    requestInFlight: false,
    hiddenAnimationComplete: false,
    voteDetailsExpanded: false,
  }),
  computed: {
    currentReviewer() {
      return this.review.reviewMembers.find(
        (reviewer) =>
          reviewer.researcher.id === this.currentResearcher.id
      );
    },
    optimisticReadyReference() {
      // When saving a vote, we need to pass in a reference that has a more complete version of `review`.
      // This is because the `review` field on the reference does not have all the information we need
      // to save the vote optimistically.
      const reference = cloneDeep(this.reference);
      reference.review = this.review;
      return reference;
    },
    voteType() {
      return this.fullTextReview
        ? FullTextReviewVote
        : ReviewerScreeningVote;
    },
    outcomeType() {
      return this.fullTextReview
        ? FullTextReviewOutcome
        : ScreeningOutcome;
    },
    outcome() {
      return this.fullTextReview
        ? this.reference.fullTextReviewOutcome
        : this.reference.screeningOutcome;
    },
    showVoteDetails() {
      return (
        this.fullTextReview &&
        (this.reference.fullTextReviewVotes.length > 0 ||
          this.reference.hasFullTextReviewOutcome())
      );
    },
  },
  methods: {
    ...mapActions({
      saveScreeningVoteToDb:
        'reviewerScreeningVote/saveOptimistically',
      revokeScreeningVoteFromDb: 'reviewerScreeningVote/delete',
      saveScreeningOutcomeToDb: 'screeningOutcome/saveOptimistically',
      resolveScreeningConflictToDb:
        'screeningOutcome/resolveConflictOptimistically',
      removeScreeningConflictResolutionFromDb:
        'screeningOutcome/removeConflictResolution',
      saveFullTextReviewVoteToDb:
        'fullTextReviewVote/saveOptimistically',
      revokeFullTextReviewVoteFromDb: 'fullTextReviewVote/delete',
      saveFullTextReviewOutcomeToDb:
        'fullTextReviewOutcome/saveOptimistically',
      resolveFullTextReviewConflictToDb:
        'fullTextReviewOutcome/resolveConflictOptimistically',
    }),
    async saveOptimistically(voteToSave) {
      return this.fullTextReview
        ? this.saveFullTextReviewVoteToDb(voteToSave)
        : this.saveScreeningVoteToDb(voteToSave);
    },
    async revokeVoteFromDb(voteToRevoke) {
      return this.fullTextReview
        ? this.revokeFullTextReviewVoteFromDb(voteToRevoke)
        : this.revokeScreeningVoteFromDb(voteToRevoke);
    },
    async saveOutcomeToDb(outcomeToSave) {
      return this.fullTextReview
        ? this.saveFullTextReviewOutcomeToDb(outcomeToSave)
        : this.saveScreeningOutcomeToDb(outcomeToSave);
    },
    async resolveConflictToDb(outcomeToResolve) {
      return this.fullTextReview
        ? this.resolveFullTextReviewConflictToDb(outcomeToResolve)
        : this.resolveScreeningConflictToDb(outcomeToResolve);
    },
    async unmountAfterRequest(request) {
      this.requestInFlight = true;
      await request();
      this.requestInFlight = false;
      if (this.hiddenAnimationComplete) {
        this.$emit('hidden');
      }
    },
    async saveVote(voteToSave) {
      this.unmountAfterRequest(async () => {
        this.$emit('saving-vote', voteToSave);
        const savedVote = await this.saveOptimistically(voteToSave);
        this.$emit('saved-vote', savedVote);
      });
    },
    async revokeVote(voteToRevoke) {
      this.unmountAfterRequest(async () => {
        this.$emit('revoking-vote', voteToRevoke);
        await this.revokeVoteFromDb(voteToRevoke);
        this.$emit('revoked-vote', voteToRevoke);
      });
    },
    async saveOutcome(outcomeToSave) {
      this.unmountAfterRequest(async () => {
        this.$emit('saving-outcome', outcomeToSave);
        const savedOutcome = await this.saveOutcomeToDb(
          outcomeToSave
        );
        this.$emit('saved-outcome', savedOutcome);
      });
    },
    async resolveConflict(outcomeToResolve) {
      this.unmountAfterRequest(async () => {
        this.$emit('saving-outcome', outcomeToResolve);
        const savedOutcome = await this.resolveConflictToDb(
          outcomeToResolve
        );
        this.$emit('saved-outcome', savedOutcome);
      });
    },
    onCreateVote(assessment) {
      let reviewCriteria;
      let notes;
      if (assessment.vote) {
        reviewCriteria = assessment.reviewCriteria;
        notes = assessment.notes;
        assessment = assessment.vote;
      }
      const voteToCreate = new this.voteType({
        reference: this.optimisticReadyReference,
        reviewer: this.currentReviewer,
        vote: assessment,
        // Full text review votes only.
        reasonReviewCriteria: reviewCriteria,
        notes,
      });
      this.saveVote(voteToCreate);
    },
    getVoteByResearcher(researcher) {
      return this.fullTextReview
        ? this.reference.getFullTextReviewVoteBy(researcher)
        : this.reference.getScreeningVoteBy(researcher);
    },
    onUpdateVote(assessment) {
      const existingVote = this.getVoteByResearcher(
        this.currentResearcher
      );
      if (!existingVote) {
        throw new Error(
          'Failed to update vote: No existing vote found'
        );
      }
      let reviewCriteria;
      let notes;
      if (assessment.vote) {
        reviewCriteria = assessment.reviewCriteria;
        notes = assessment.notes;
        assessment = assessment.vote;
      }
      const voteToSave = new this.voteType({
        ...existingVote,
        reference: this.optimisticReadyReference,
        vote: assessment,
        // Full text review votes only.
        reasonReviewCriteria: reviewCriteria,
        notes,
      });
      this.saveVote(voteToSave);
    },
    onRevokeVote() {
      const existingVote = this.getVoteByResearcher(
        this.currentResearcher
      );
      if (!existingVote) {
        throw new Error(
          'Failed to update vote: No existing vote found'
        );
      }
      this.revokeVote(existingVote);
    },
    onResolveConflict(outcome) {
      let reviewCriteria;
      let notes;
      if (outcome.vote) {
        reviewCriteria = outcome.reviewCriteria;
        notes = outcome.notes;
        outcome = outcome.vote;
      }
      this.resolveConflict({
        reference: this.optimisticReadyReference,
        outcome,
        resolvedBy: this.currentReviewer,
        // Full text review outcomes only.
        reasonReviewCriteria: reviewCriteria,
        notes,
      });
    },
    onUpdateOutcome(assessment) {
      const existingOutcome = this.outcome;
      if (!existingOutcome) {
        throw new Error(
          'Failed to update outcome: No existing outcome found'
        );
      }
      let reviewCriteria;
      let notes;
      if (assessment.vote) {
        reviewCriteria = assessment.reviewCriteria;
        notes = assessment.notes;
        assessment = assessment.vote;
      }
      this.saveOutcome(
        new this.outcomeType({
          ...existingOutcome,
          reference: this.optimisticReadyReference,
          outcome: assessment,
          resolution: ScreeningOutcomeResolution.UPDATED_OUTCOME,
          resolvedBy: this.currentReviewer,
          // Full text review outcomes only.
          reasonReviewCriteria: reviewCriteria,
          notes,
        })
      );
    },
    onHiddenAnimationComplete() {
      this.hiddenAnimationComplete = true;
      // No request in flight means we can emit the hidden event.
      if (!this.requestInFlight) {
        this.$emit('hidden');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.vote-details-section {
  border-top: 1px solid #eeeeee;

  .expand-button {
    cursor: pointer;
    font-size: 0.875rem;
  }
}
</style>
